import { useEffect, useMemo, useState } from "react";
import { BookOpenCheck, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { resolveDisclosureText } from "@/lib/amazonAffiliate";
import { Badge } from "@/components/ui/badge";

import {
  getCitationStyleLabel,
  isNoteStyleHeuristic,
  renderCitation,
  renderCitationSequence,
  resolveCitationStyle,
} from "@/lib/citationStyles";
import { toCslJson } from "@/lib/citations";
import type { LibraryItem } from "@/lib/library";
import { OpenAccessLink } from "@/components/library/OpenAccessLink";
import type { ContentBriefWithReferences, ResolvedBriefReference } from "@/lib/composerCore";
import {
  useCitationsPreviewData,
  wrapTitleWithAffiliateLink,
} from "./citationsPreview/useCitationsPreviewData";
import type { BriefDocument, BlockNode, InlineNode } from "@/lib/briefDocument/types";
import { footnoteBodyId, footnoteRefId } from "@/lib/briefDocument/footnoteAnchors";
import { footnoteBodyPlainText, renderFootnoteBodyReact } from "@/lib/briefDocument/footnoteBody";


type Props = {
  workspaceId: string;
  /**
   * The Piece (content_pieces row) whose citations we are previewing.
   * Typed as ContentBriefWithReferences for back-compat with the legacy
   * Composer data layer (see mem://features/composer-vocabulary.md).
   */
  piece: ContentBriefWithReferences;
  /**
   * Optional Draft document. When provided, the panel renders a "Notes" block
   * at the bottom listing all footnoteRef + endnoteRef bodies in document
   * order with stable backref anchors (id={footnoteBodyId(id)}, ↩ link to
   * #footnoteRefId(id)). Numbering matches the published renderer.
   */
  draftDoc?: BriefDocument | null;
  onChanged?: () => void | Promise<void>;
};

/**
 * Walk a Draft document and collect footnote/endnote refs in document order,
 * dedup by attrs.id, assign 1-based numbers. Mirrors the pre-walk used by
 * renderBriefDocument so anchors here line up with anchors in published HTML.
 */
type CollectedNote = {
  id: string;
  number: number;
  body: string;
  kind: "footnote" | "endnote";
  refId: string | null;
  locator: string | null;
};
const collectNotes = (doc: BriefDocument | null | undefined): CollectedNote[] => {
  if (!doc) return [];
  const seen = new Set<string>();
  const out: CollectedNote[] = [];
  const visitInline = (nodes: InlineNode[] | undefined) => {
    if (!nodes) return;
    for (const n of nodes) {
      if (n.type === "excerptus.footnoteRef" || n.type === "excerptus.endnoteRef") {
        const id = n.attrs.id;
        if (!id || seen.has(id)) continue;
        seen.add(id);
        out.push({
          id,
          number: out.length + 1,
          body: n.attrs.body ?? "",
          kind: n.type === "excerptus.footnoteRef" ? "footnote" : "endnote",
          refId: (n.attrs as { refId?: string | null }).refId ?? null,
          locator: (n.attrs as { locator?: string | null }).locator ?? null,
        });
      }
    }
  };
  const visitBlock = (b: BlockNode) => {
    if (b.type === "paragraph" || b.type === "heading") visitInline(b.content);
    else if (b.type === "blockquote") b.content?.forEach((p) => visitInline(p.content));
    else if (b.type === "bulletList" || b.type === "orderedList") {
      b.content?.forEach((li) => li.content?.forEach((p) => visitInline(p.content)));
    }
  };
  doc.content?.forEach(visitBlock);
  return out;
};

/**
 * Renders a citation preview block beneath the Draft Canvas. Pulls the
 * workspace citation style + the Piece's source references, separates them
 * into "cited" (in-text footnotes/endnotes) and the merged Bibliography
 * (cited + consulted, deduped, alphabetized — labeled simply "Bibliography"),
 * and shows footnotes/endnotes per the Piece's `metadata.citationMode`.
 */
export const CitationsPreviewPanel = ({ workspaceId, piece, draftDoc }: Props) => {
  const {
    sourceRefs, style, items: refItems, loading, disclosureText,
    cited, consulted, affiliateHrefFor,
  } = useCitationsPreviewData(workspaceId, piece);


  const notes = useMemo(() => collectNotes(draftDoc), [draftDoc]);

  // Supplement the items map with library items referenced by note refIds
  // that aren't in the piece bibliography yet (e.g. just attached in the
  // popover, snapshot refresh not yet propagated).
  const [extraItems, setExtraItems] = useState<Map<string, LibraryItem>>(new Map());
  const missingNoteRefIds = useMemo(() => {
    const ids = new Set<string>();
    for (const n of notes) {
      if (n.refId && !refItems.has(n.refId) && !extraItems.has(n.refId)) ids.add(n.refId);
    }
    return Array.from(ids);
  }, [notes, refItems, extraItems]);
  useEffect(() => {
    if (missingNoteRefIds.length === 0) return;
    let cancelled = false;
    void (async () => {
      const { data } = await supabase
        .from("library_items")
        .select("*")
        .in("id", missingNoteRefIds);
      if (cancelled || !data) return;
      setExtraItems((prev) => {
        const next = new Map(prev);
        for (const row of data as LibraryItem[]) next.set(row.id, row);
        return next;
      });
    })();
    return () => { cancelled = true; };
  }, [missingNoteRefIds]);
  const items = useMemo(() => {
    const merged = new Map(refItems);
    for (const [id, item] of extraItems) if (!merged.has(id)) merged.set(id, item);
    return merged;
  }, [refItems, extraItems]);

  // When style is Chicago and any note carries a refId, re-render
  // bodies via citeproc as one sequence so first/short/Ibid rules apply
  // natively. Notes without a refId keep their stored body verbatim.
  const [resolvedNoteBodyHtml, setResolvedNoteBodyHtml] = useState<Map<string, string>>(
    new Map(),
  );
  useEffect(() => {
    if (!isNoteStyleHeuristic(style)) {
      setResolvedNoteBodyHtml(new Map());
      return;
    }
    const indexed: { id: string; csl: import("@/lib/citations").CslItem; locator: string | null }[] = [];
    for (const n of notes) {
      if (!n.refId) continue;
      const item = items.get(n.refId);
      if (!item) continue;
      indexed.push({ id: n.id, csl: toCslJson(item), locator: n.locator });
    }
    if (!indexed.length) {
      setResolvedNoteBodyHtml(new Map());
      return;
    }
    let cancelled = false;
    void (async () => {
      const rendered = await renderCitationSequence(
        style,
        indexed.map(({ csl, locator }) => ({ csl, locator })),
      );
      if (cancelled) return;
      const map = new Map<string, string>();
      indexed.forEach((entry, i) => map.set(entry.id, rendered[i]?.html ?? ""));
      setResolvedNoteBodyHtml(map);
    })();
    return () => { cancelled = true; };
  }, [notes, items, style]);




  // Project rule: Bibliography is one section labeled "Bibliography". The
  // Bibliography is works-consulted by inclusion; cited works are evidenced
  // by the footnotes/endnotes above. Merge cited + consulted, dedupe by id,
  // alphabetize via the renderer-mirroring sort below.
  const bibliographyHeading = "Bibliography";
  const bibliographyEntries = useMemo<ResolvedBriefReference[]>(() => {
    const seen = new Set<string>();
    const merged: ResolvedBriefReference[] = [];
    for (const r of [...cited, ...consulted]) {
      if (seen.has(r.id)) continue;
      seen.add(r.id);
      merged.push(r);
    }
    return merged;
  }, [cited, consulted]);

  const hasAffiliateLinks = useMemo(() => {
    return bibliographyEntries.some((ref) => {
      const item = items.get(ref.reference_id);
      return !!item && !!affiliateHrefFor(item);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bibliographyEntries, items]);

  const hasSources = sourceRefs.length > 0;
  const hasNotes = notes.length > 0;

  // NOTE: keep all hooks above any early return so React's hook order
  // stays stable across renders (react-hooks/rules-of-hooks).
  const footnoteBodies = useMemo(() => notes.filter((n) => n.kind === "footnote"), [notes]);
  const endnoteBodies = useMemo(() => notes.filter((n) => n.kind === "endnote"), [notes]);

  // Pre-render bibliography HTML asynchronously (citeproc is lazy-loaded).
  // Keyed by ref.id so render() can do a synchronous map lookup.

  const [bibHtmlMap, setBibHtmlMap] = useState<Map<string, string>>(new Map());
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const next = new Map<string, string>();
      await Promise.all(
        bibliographyEntries.map(async (ref) => {
          const item = items.get(ref.reference_id);
          if (!item) return;
          const csl = toCslJson(item);
          const itemStyle = resolveCitationStyle(
            style,
            (item as { citation_style_override?: string | null }).citation_style_override,
          );
          const rendered = await renderCitation(csl, { style: itemStyle, mode: "bibliography" });
          const href = affiliateHrefFor(item);
          next.set(ref.id, href ? wrapTitleWithAffiliateLink(rendered.html, href) : rendered.html);
        }),
      );
      if (!cancelled) setBibHtmlMap(next);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bibliographyEntries, items, style]);

  if (!hasSources && !hasNotes) {
    return (
      <div className="space-y-2">
        <p className="text-xs uppercase tracking-[0.08em] text-muted-foreground">Citations Preview</p>
        <div className="rounded-md border border-dashed border-border p-4 text-sm text-muted-foreground">
          No linked sources and no footnotes yet. Add a source reference or insert a
          footnote in the Draft Canvas to preview citations here.
        </div>
      </div>
    );
  }

  const renderNoteListItem = (n: CollectedNote) => (
    <li
      key={n.id}
      id={footnoteBodyId(n.id)}
      className="text-sm leading-6 text-foreground scroll-mt-24"
    >
      <span className="mr-1 font-medium text-muted-foreground">[{n.number}]</span>
      {(() => {
        const rawResolved = resolvedNoteBodyHtml.get(n.id);
        if (rawResolved) {
          const item = n.refId ? items.get(n.refId) : undefined;
          const href = item ? affiliateHrefFor(item) : null;
          const resolvedHtml = href ? wrapTitleWithAffiliateLink(rawResolved, href) : rawResolved;
          const cite = <span dangerouslySetInnerHTML={{ __html: resolvedHtml }} />;
          const bodyText = footnoteBodyPlainText(n.body ?? "").trim();
          // Treat empty / whitespace-only bodies (including stray `<p></p>` from
          // the WYSIWYG editor) as no body — otherwise we appended ". " after a
          // cite that already ends in "." and produced a visible double period.
          if (!bodyText) return cite;
          const citeText = footnoteBodyPlainText(resolvedHtml).trim().replace(/\.\s*$/, "");
          const isDuplicate =
            bodyText.replace(/\.\s*$/, "") === citeText ||
            bodyText.replace(/\.\s*$/, "") === citeText.replace(/,\s*[^,]+$/, "");
          if (isDuplicate) return cite;
          // Strip one trailing period+whitespace from cite before joining so the
          // combined "cite. body" only ever has a single period between them.
          const joinedHtml = resolvedHtml.replace(/\.(\s|&nbsp;)*$/, "");
          const citeJoined = <span dangerouslySetInnerHTML={{ __html: joinedHtml }} />;
          return (
            <>
              {citeJoined}
              {". "}
              {renderFootnoteBodyReact(n.body)}
            </>
          );
        }
        return n.body ? renderFootnoteBodyReact(n.body) : <span className="italic text-muted-foreground">(empty)</span>;
      })()}{" "}

      <a
        href={`#${footnoteRefId(n.id)}`}
        aria-label="Back to reference"
        className="text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
        onClick={(e) => {
          e.preventDefault();
          document
            .getElementById(footnoteRefId(n.id))
            ?.scrollIntoView({ behavior: "smooth", block: "center" });
        }}
      >
        ↩
      </a>
    </li>
  );

  const renderBibEntry = (ref: ResolvedBriefReference) => {
    const item = items.get(ref.reference_id);
    if (!item) {
      return (
        <li key={ref.id} className="text-sm text-muted-foreground">
          {ref.label} <span className="italic">(library item unavailable)</span>
        </li>
      );
    }
    const html = bibHtmlMap.get(ref.id);
    const oaUrl = (item as { open_access_url?: string | null }).open_access_url ?? null;
    return (
      <li
        key={ref.id}
        className="text-sm leading-6 text-foreground [text-indent:-1.5rem] pl-6"
      >
        {html ? (
          <span dangerouslySetInnerHTML={{ __html: html }} />
        ) : (
          <span className="text-muted-foreground">{ref.label}</span>
        )}
        <OpenAccessLink href={oaUrl} variant="inline" />
      </li>
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <BookOpenCheck className="h-4 w-4 text-primary" />
          <p className="text-xs uppercase tracking-[0.08em] text-muted-foreground">Citations Preview</p>
          <Badge variant="outline" className="font-normal">
            {getCitationStyleLabel(style)}
          </Badge>
        </div>
      </div>


      <div className="rounded-md border border-border bg-background p-4 space-y-5">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading library items…
          </div>
        ) : (
          <>
            {footnoteBodies.length > 0 && (
              <section className="space-y-2">
                <h4 className="text-sm font-semibold text-foreground">Footnotes</h4>
                <ol className="space-y-1.5 list-none">
                  {footnoteBodies.map(renderNoteListItem)}
                </ol>
              </section>
            )}

            {endnoteBodies.length > 0 && (
              <section className="space-y-2 border-t border-border/60 pt-3">
                <h4 className="text-sm font-semibold text-foreground">Endnotes</h4>
                <ol className="space-y-1.5 list-none">
                  {endnoteBodies.map(renderNoteListItem)}
                </ol>
              </section>
            )}

            {bibliographyEntries.length > 0 && (
              <section className="space-y-2 border-t border-border/60 pt-3">
                <h4 className="text-sm font-semibold text-foreground">{bibliographyHeading}</h4>
                <ul className="space-y-1.5 list-none">{bibliographyEntries.map(renderBibEntry)}</ul>
              </section>
            )}

            {hasAffiliateLinks && (
              <section className="space-y-1 border-t border-border/60 pt-3">
                <h4 className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                  Disclosure
                </h4>
                <p className="text-xs leading-5 text-muted-foreground">
                  {resolveDisclosureText(disclosureText)}
                </p>
              </section>
            )}
          </>
        )}
      </div>

      <p className="text-[11px] text-muted-foreground">
        Style follows the workspace default. Per-source overrides apply automatically when set on a library item.
      </p>
      <p className="text-[11px] text-muted-foreground">
        Citations rendered with the{" "}
        <a
          href="/legal/citation-styles/"
          target="_blank"
          rel="noreferrer"
          className="underline hover:text-foreground"
        >
          CSL Style Repository
        </a>{" "}
        (CC BY-SA 3.0).
      </p>
    </div>
  );
};
